/**
 * InfinitSession - Client-side session management for Shopify
 *
 * Provides persistent session storage across page loads using:
 * - Anonymous users: UUID stored in localStorage
 * - Logged-in users: Shopify customer ID (persistent)
 *
 * Usage:
 *   await InfinitSession.init();                              // Initialize on page load
 *   await InfinitSession.set('quiz_progress', { step: 3 });   // Set variable
 *   const progress = await InfinitSession.get('quiz_progress'); // Get variable
 *   await InfinitSession.clear('quiz_progress');              // Clear variable
 *   InfinitSession.enableDiagnostics();                       // Enable console logging
 *   InfinitSession.getSessionInfo();                          // View session state
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'infinit_session_id';
  const API_BASE = '/apps/formulas/session';

  let sessionState = {
    initialized: false,
    id: null,
    type: null,
    data: {},
    isLoggedIn: false,
    customerId: null,
    shopDomain: null
  };

  let diagnosticsEnabled = false;
  let initPromise = null;

  // ============================================================================
  // Utility Functions
  // ============================================================================

  function generateUUID() {
    // Generate a UUID v4
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback for older browsers
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  function getAnonymousId() {
    let id = localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = generateUUID();
      localStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  }

  function log(message, data) {
    if (!diagnosticsEnabled) return;

    const prefix = '[InfinitSession]';
    if (data !== undefined) {
      console.log(prefix, message);
      if (typeof data === 'object' && data !== null) {
        console.table ? console.table(data) : console.log(data);
      } else {
        console.log('  ', data);
      }
    } else {
      console.log(prefix, message);
    }
  }

  function logSessionState() {
    if (!diagnosticsEnabled) return;

    console.log('[InfinitSession] Session initialized');
    console.log('  type:', JSON.stringify(sessionState.type));
    console.log('  id:', JSON.stringify(sessionState.id));
    console.log('  data:', sessionState.data);
  }

  // ============================================================================
  // API Functions
  // ============================================================================

  async function apiRequest(endpoint, method, body) {
    const url = `${API_BASE}/${endpoint}`;
    const options = {
      method: method,
      headers: {
        'Content-Type': 'application/json'
      }
    };

    if (body) {
      // Always include anonymous_id for session identification
      if (!sessionState.isLoggedIn) {
        body.anonymous_id = getAnonymousId();
      }
      options.body = JSON.stringify(body);
    }

    if (diagnosticsEnabled) {
      log(`API ${method} ${endpoint}`, body || '(no body)');
    }

    try {
      const response = await fetch(url, options);
      const result = await response.json();

      if (!result.ok) {
        throw new Error(result.message || 'API request failed');
      }

      return result;
    } catch (error) {
      log('API Error:', error.message);
      throw error;
    }
  }

  async function apiGet(endpoint, params = {}) {
    let url = `${API_BASE}/${endpoint}`;

    // Add anonymous_id for anonymous sessions
    if (!sessionState.isLoggedIn) {
      params.anonymous_id = getAnonymousId();
    }

    const queryString = new URLSearchParams(params).toString();
    if (queryString) {
      url += '?' + queryString;
    }

    if (diagnosticsEnabled) {
      log(`API GET ${endpoint}`, params);
    }

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const result = await response.json();

      if (!result.ok) {
        throw new Error(result.message || 'API request failed');
      }

      return result;
    } catch (error) {
      log('API Error:', error.message);
      throw error;
    }
  }

  // ============================================================================
  // Public API
  // ============================================================================

  const InfinitSession = {
    /**
     * Initialize the session.
     * Automatically called on DOMContentLoaded if autoInit is true.
     * Returns a promise that resolves when initialization is complete.
     */
    async init() {
      // Return existing promise if already initializing
      if (initPromise) {
        return initPromise;
      }

      initPromise = this._doInit();
      return initPromise;
    },

    async _doInit() {
      try {
        // Get config from Liquid injection
        const config = window.__INFINIT_SESSION_CONFIG || {};

        sessionState.isLoggedIn = !!config.isLoggedIn;
        sessionState.customerId = config.customerId || null;
        sessionState.shopDomain = config.shopDomain || window.Shopify?.shop || '';

        // Check URL params for debug mode
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('infinit_debug') === '1') {
          this.enableDiagnostics();
        } else if (urlParams.get('infinit_debug') === '0') {
          this.disableDiagnostics();
        }

        // Build init request body
        const body = {};
        if (!sessionState.isLoggedIn) {
          body.anonymous_id = getAnonymousId();
        }

        const result = await apiRequest('init', 'POST', body);

        sessionState.id = result.session.id;
        sessionState.type = result.session.type;
        sessionState.data = result.session.data || {};
        sessionState.initialized = true;

        logSessionState();

        // Fire ready event
        window.dispatchEvent(new CustomEvent('infinit:session:ready', {
          detail: {
            id: sessionState.id,
            type: sessionState.type,
            data: sessionState.data
          }
        }));

        return sessionState;
      } catch (error) {
        log('Initialization failed:', error.message);
        sessionState.initialized = false;
        throw error;
      }
    },

    /**
     * Set one or more session variables.
     * @param {string|object} keyOrObject - Key name or object of key-value pairs
     * @param {*} value - Value (only used if keyOrObject is a string)
     */
    async set(keyOrObject, value) {
      if (!sessionState.initialized) {
        await this.init();
      }

      let values;
      if (typeof keyOrObject === 'string') {
        values = { [keyOrObject]: value };
      } else {
        values = keyOrObject;
      }

      const result = await apiRequest('set', 'POST', { values });

      sessionState.data = result.data || {};

      if (diagnosticsEnabled) {
        log('Session data updated', sessionState.data);
      }

      return sessionState.data;
    },

    /**
     * Get session variable(s).
     * @param {string|string[]} keys - Optional key or array of keys to retrieve
     * @returns {*} Value for single key, or object of values for multiple/all keys
     */
    async get(keys) {
      if (!sessionState.initialized) {
        await this.init();
      }

      // For simple reads, use cached data
      if (!keys) {
        return sessionState.data;
      }

      if (typeof keys === 'string') {
        // Single key - return from cache
        return sessionState.data[keys];
      }

      // Array of keys - filter from cache
      const result = {};
      for (const key of keys) {
        if (key in sessionState.data) {
          result[key] = sessionState.data[key];
        }
      }
      return result;
    },

    /**
     * Refresh session data from server.
     * Useful after external changes.
     */
    async refresh() {
      if (!sessionState.initialized) {
        await this.init();
        return sessionState.data;
      }

      const result = await apiGet('get');
      sessionState.data = result.data || {};

      if (diagnosticsEnabled) {
        log('Session data refreshed', sessionState.data);
      }

      return sessionState.data;
    },

    /**
     * Clear session variable(s).
     * @param {string|string[]} keys - Optional key or array of keys to clear. If omitted, clears all.
     */
    async clear(keys) {
      if (!sessionState.initialized) {
        await this.init();
      }

      const body = {};

      if (keys) {
        body.keys = Array.isArray(keys) ? keys : [keys];
      }

      const result = await apiRequest('clear', 'POST', body);

      sessionState.data = result.data || {};

      if (diagnosticsEnabled) {
        log('Session data cleared', sessionState.data);
      }

      return sessionState.data;
    },

    /**
     * Enable diagnostic console output.
     */
    enableDiagnostics() {
      diagnosticsEnabled = true;
      localStorage.setItem('infinit_session_debug', '1');
      log('Diagnostics enabled');

      if (sessionState.initialized) {
        logSessionState();
      }
    },

    /**
     * Disable diagnostic console output.
     */
    disableDiagnostics() {
      log('Diagnostics disabled');
      diagnosticsEnabled = false;
      localStorage.removeItem('infinit_session_debug');
    },

    /**
     * Get current session info (for debugging).
     */
    getSessionInfo() {
      const info = {
        initialized: sessionState.initialized,
        id: sessionState.id,
        type: sessionState.type,
        isLoggedIn: sessionState.isLoggedIn,
        customerId: sessionState.customerId,
        data: sessionState.data
      };

      if (diagnosticsEnabled) {
        log('Session info', info);
      }

      return info;
    },

    /**
     * Check if session is initialized.
     */
    isInitialized() {
      return sessionState.initialized;
    },

    /**
     * Check if user is logged in.
     */
    isLoggedIn() {
      return sessionState.isLoggedIn;
    },

    /**
     * Get session type ('anonymous' or 'customer').
     */
    getType() {
      return sessionState.type;
    },

    /**
     * Get session ID.
     */
    getId() {
      return sessionState.id;
    }
  };

  // Check for persisted debug mode
  if (localStorage.getItem('infinit_session_debug') === '1') {
    diagnosticsEnabled = true;
  }

  // Auto-initialize on DOMContentLoaded if configured
  function autoInit() {
    const config = window.__INFINIT_SESSION_CONFIG || {};
    if (config.autoInit !== false) {
      InfinitSession.init().catch(function(error) {
        console.error('[InfinitSession] Auto-init failed:', error.message);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    // DOM already loaded
    autoInit();
  }

  // Expose globally
  window.InfinitSession = InfinitSession;
})();
